import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';
import ConfirmModal from '../../components/ConfirmModal';

const EditThingsToDo = () => {
  const { locationId, id } = useParams();
  const navigate = useNavigate();
  const [locationName, setLocationName] = useState('');
  const [showBackModal, setShowBackModal] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    duration: '',
    address: ''
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();

    window.history.pushState(null, '', window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, '', window.location.href);
      setShowBackModal(true);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [id]);

  const fetchData = async () => {
    try {
      const response = await apiService.get(`getThingsToDo/${id}`);
      if (response.status) {
        setLocationName(response.data.locationName);
        const item = response.data.data;
        setFormData({
          title: item.title || '',
          description: item.description || '',
          duration: item.duration || '',
          address: item.address || ''
        });
      }
    } catch (error) {
      toast.error('Failed to load data');
      console.error('Fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const response = await apiService.post('updateThingsToDo', {
        ...formData,
        id,
        locationId
      });

      if (response.status) {
        toast.success('Things To Do updated successfully!');
        navigate(`/things-to-do/${locationId}`);
      } else {
        toast.error(response.message || 'Failed to update');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error('Submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
        <span className="ml-2 text-gray-600">Loading...</span>
      </div>
    );
  }

  return (
    <div className="p-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <nav className="text-xs sm:text-sm flex flex-wrap items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/location" className="text-gray-500 hover:text-gray-700 no-underline">Locations</Link>
          <span className="mx-2 text-gray-400">/</span>
          <Link to={`/things-to-do/${locationId}`} className="text-gray-500 hover:text-gray-700 no-underline">Things To Do</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Edit</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-3 sm:p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Edit Things To Do {locationName && `- ${locationName}`}
          </h2>
        </div>

        <div className="p-4 sm:p-6">
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Title <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                  placeholder="Enter title"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description <span className="text-red-500">*</span></label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                  placeholder="Enter description"
                  rows="5"
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Duration <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                    placeholder="e.g., 2-3 hours"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#009ef7]/20 focus:border-[#009ef7] hover:border-gray-300 outline-none transition-all duration-200 bg-white"
                    placeholder="Enter address"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-3 mt-6 border-t pt-4">
              <button
                type="button"
                onClick={() => setShowBackModal(true)}
                disabled={submitting}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-white rounded-lg disabled:opacity-50 text-sm font-medium transition-colors"
                style={{ backgroundColor: '#009ef7' }}
              >
                {submitting ? (
                  <>
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white inline-block mr-2"></span>
                    Please wait...
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      <ConfirmModal
        isOpen={showBackModal}
        title="Are you sure you want to go back?"
        message="Any unsaved changes to this Things To Do item will be lost."
        confirmText="Yes, Go Back"
        cancelText="Stay Here"
        onConfirm={() => navigate(`/things-to-do/${locationId}`)}
        onCancel={() => setShowBackModal(false)}
      />
    </div>
  );
};

export default EditThingsToDo;
